import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { erpApi } from '../../api/erp';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input, Select } from '../../components/ui/Input';
import './erp.css';

interface CreatedUser {
  email: string;
  password: string;
  emailSent: boolean;
  emailError?: string;
}

export function ErpCreateUserPage() {
  const [form, setForm] = useState<{
    email: string;
    name: string;
    surname: string;
    representationEntityType: 'fisica' | 'juridica';
    representativeNif: string;
  }>({
    email: '',
    name: '',
    surname: '',
    representationEntityType: 'fisica',
    representativeNif: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState<CreatedUser | null>(null);
  const [copied, setCopied] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await erpApi.createUser(form);
      setCreated({
        email: result.user.email,
        password: result.provisional_password,
        emailSent: result.email_sent,
        emailError: result.email_error,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear el cliente');
    } finally {
      setLoading(false);
    }
  }

  async function copyPassword() {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // El navegador puede bloquear el portapapeles; el admin siempre puede copiar a mano.
    }
  }

  function reset() {
    setForm({
      email: '',
      name: '',
      surname: '',
      representationEntityType: 'fisica',
      representativeNif: '',
    });
    setCreated(null);
    setCopied(false);
  }

  return (
    <div>
      <p style={{ marginBottom: '1rem' }}>
        <Link to="/erp/clientes">← Volver a clientes</Link>
      </p>
      <Card title="Nuevo cliente">
        {created ? (
          <div className="erp-form-grid">
            <p style={{ color: 'var(--color-success)', margin: 0 }}>
              Cliente creado correctamente.
            </p>
            <p style={{ margin: 0 }}>
              Contraseña provisional para <strong>{created.email}</strong>. Se mostrará
              <strong> una sola vez</strong>: cópiala ahora si la necesitas.
            </p>
            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <code
                style={{
                  fontSize: '1.05rem',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.4rem',
                  background: 'var(--color-surface-2, rgba(0,0,0,0.06))',
                  letterSpacing: '0.03em',
                }}
              >
                {created.password}
              </code>
              <Button type="button" size="sm" variant="ghost" onClick={copyPassword}>
                {copied ? 'Copiada ✓' : 'Copiar'}
              </Button>
            </div>
            <p className="muted" style={{ margin: 0, fontSize: '0.9rem' }}>
              {created.emailSent
                ? 'Se ha enviado también por email al cliente.'
                : `No se pudo enviar el email al cliente${
                    created.emailError ? ` (${created.emailError})` : ''
                  }. Comunícale la contraseña manualmente.`}
            </p>
            <p className="muted" style={{ margin: 0, fontSize: '0.9rem' }}>
              El cliente deberá cambiarla en su primer inicio de sesión.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button type="button" onClick={reset}>
                Crear otro cliente
              </Button>
              <Link to="/erp/clientes">
                <Button type="button" variant="ghost">
                  Ir a clientes
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <form className="erp-form-grid" onSubmit={handleSubmit}>
            <div className="grid-2">
              <Input
                label="Nombre"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                required
              />
              <Input
                label="Apellidos"
                value={form.surname}
                onChange={(e) => setForm((p) => ({ ...p, surname: e.target.value }))}
                required
              />
            </div>
            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              required
              hint="Se generará una contraseña provisional segura y se enviará a este correo"
            />
            <Select
              label="Tipo de titular fiscal"
              value={form.representationEntityType}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  representationEntityType: event.target.value as 'fisica' | 'juridica',
                }))
              }
              options={[
                { value: 'fisica', label: 'Persona física / autónomo' },
                { value: 'juridica', label: 'Persona jurídica / sociedad' },
              ]}
            />
            <Input
              label={
                form.representationEntityType === 'juridica'
                  ? 'NIF del representante legal'
                  : 'NIF del titular'
              }
              value={form.representativeNif}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  representativeNif: event.target.value.toUpperCase(),
                }))
              }
              maxLength={20}
              required
            />
            {error ? <p style={{ color: 'var(--color-danger)', margin: 0 }}>{error}</p> : null}
            <Button type="submit" disabled={loading}>
              {loading ? 'Creando…' : 'Crear cliente'}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
